import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import FriendName from './FriendName';
import FriendEmail from './FriendEmail';
import FriendMobile from './FriendMobile';
import HookSessionFeeSelect from '../HookSessionFeeSelect';
import FriendIcon from '../../../Icons/FriendIcon';
import { loadHookEventsFrontendData } from '../../../../Util/HookEventsFrontendData';

const HookEventFriendInputs = (props) => {
	const { t } = useTranslation();
	const { formFields } = props;
	const fv = useSelector(state => state.fv);

	const [friendCount, setFriendCount] = useState(0);
	const [manualFriendCount, setManualFriendCount] = useState(0);

	// Load session data from HookEvents AJAX endpoint
	const loadSessionDataFromAPI = async () => {
		try {
			// Get funnel_page_id from props or DOM
			const funnelPageId = props.funnel_page_id || document.querySelector('[data-funnel-page-id]')?.getAttribute('data-funnel-page-id');
			const lang = props.lang || 'zh-TW';
			
			if (!funnelPageId) {
				// Set fallback data when no funnel_page_id
				const fallbackData = {
					loaded: true,
					checkout_page: false,
					num_participants: 1,
					reg_multiple_participants: true,
					requires_friend_contacts: true,
					one_session_display: false
				};
				setSessionData(fallbackData);
				return;
			}
			
			try {
				const apiData = await loadHookEventsFrontendData(funnelPageId, lang);

				// Check if there are any tickets selected
				// Look for hidden input fields with the new structure: page_user_hook_session[hook_session_fees][sessionFeeId][qty]
				const qtyInputs = document.querySelectorAll('input.qty-hidden[name*="page_user_hook_session[hook_session_fees]"][name*="[qty]"]');
				let totalSelected = 0;
				qtyInputs.forEach(input => {
					const qty = parseInt(input.value, 10);
					if (!isNaN(qty) && qty > 0) {
						totalSelected += qty;
					}
				});

				let checkoutPage = apiData.checkout_page || false;
				if (checkoutPage && totalSelected === 0) {
					checkoutPage = false;
				}

				const newSessionData = {
					loaded: true,
					checkout_page: checkoutPage,
					num_participants: (apiData.num_participants !== undefined && apiData.num_participants !== null) ? apiData.num_participants : 1,
					reg_multiple_participants: apiData.reg_multiple_participants !== undefined ? apiData.reg_multiple_participants : true,
					requires_friend_contacts: apiData.requires_friend_contacts !== undefined ? apiData.requires_friend_contacts : true,
					one_session_display: apiData.one_session_display || false
				};

				setSessionData(newSessionData);
			} catch (hookEventsError) {
				// HookEvents API failed, set fallback data
				throw new Error('HookEvents API failed');
			}
		} catch (error) {
			// Set fallback data on API failure
			const fallbackData = {
				loaded: true,
				checkout_page: false,
				num_participants: 1,
				reg_multiple_participants: true,
				requires_friend_contacts: true,
				one_session_display: false
			};
			setSessionData(fallbackData);
		}
	};

	// State to track session data changes
	const [sessionData, setSessionData] = useState({
		loaded: false,
		checkout_page: false,
		num_participants: 1,
		reg_multiple_participants: true,
		requires_friend_contacts: true,
		one_session_display: false
	});

	// Load session data from API on component mount（先等 Turnstile token，或短暫超時回退）
	useEffect(() => {
		let didLoad = false;
		const tryLoad = () => {
			if (didLoad) return;
			didLoad = true;
			loadSessionDataFromAPI();
		};

		if (window.turnstileToken) {
			tryLoad();
		} else {
			const onReady = () => tryLoad();
			document.addEventListener('turnstileReady', onReady, { once: true });
			setTimeout(tryLoad, 2000);
			return () => {
				document.removeEventListener('turnstileReady', onReady);
			};
		}
	}, []);

	// Get total selected participants from SessionFeeQty components
	const getTotalSelectedParticipants = () => {
		// Look for hidden input fields with the new structure: page_user_hook_session[hook_session_fees][sessionFeeId][qty]
		const qtyInputs = document.querySelectorAll('input.qty-hidden[name*="page_user_hook_session[hook_session_fees]"][name*="[qty]"]');
		let totalSelected = 0;
		
		qtyInputs.forEach(input => {
			const qty = parseInt(input.value, 10);
			if (!isNaN(qty) && qty > 0) {
				totalSelected += qty;
			}
		});
		
		return totalSelected;
	};

	// Check if any selected session fee has requiresFriendContacts as false
	const checkRequiresFriendContacts = () => {
		// Look for count divs with data-requires-friend-contacts attribute
		const countDivs = document.querySelectorAll('.count[data-requires-friend-contacts]');
		
		for (let countDiv of countDivs) {
			const requiresFriendContacts = countDiv.getAttribute('data-requires-friend-contacts');
			
			// Find the corresponding qty input for this count div
			const qtyInput = countDiv.querySelector('input.qty-hidden');
			if (qtyInput) {
				const qty = parseInt(qtyInput.value, 10);
				
				// If this fee has a qty > 0 and requiresFriendContacts is false, return true
				if (qty > 0 && requiresFriendContacts === 'false') {
					return true;
				}
			}
		}
		return false;
	};

	// Update friend count when selected participants change
	const updateFriendCount = () => {
		if (sessionData.checkout_page) {
			// Checkout page mode: use SessionFeeQty components
			const totalSelected = getTotalSelectedParticipants();
			const hasAnyRequiresFriendContactsFalse = checkRequiresFriendContacts();
			
			// If have tix (totalSelected > 0), show friend list based on (qty selected - 1)
			if (totalSelected > 0 && sessionData.requires_friend_contacts && !hasAnyRequiresFriendContactsFalse) {
				// Friend count = total selected - 1 (excluding the main participant)
				const newFriendCount = Math.max(0, totalSelected - 1);
				setFriendCount(newFriendCount);
			} else {
				setFriendCount(0);
			}
		} else {
			// Non-checkout page mode: use manual selection directly
			if (sessionData.requires_friend_contacts) {
				setFriendCount(manualFriendCount);
			} else {
				setFriendCount(0);
			}
		}
	};

	// Handle manual friend count change
	const handleManualFriendCountChange = (e) => {
		const newValue = parseInt(e.target.value, 10);
		setManualFriendCount(newValue);
		setFriendCount(newValue); // Directly set friend count based on selection
	};

	// Update friend count when dependencies change
	useEffect(() => {
		updateFriendCount();
	}, [manualFriendCount, sessionData.loaded, sessionData.checkout_page, sessionData.requires_friend_contacts, sessionData.num_participants]);

	// Trigger form validation reinitialization when friend count changes
	useEffect(() => {
		// Clean up validation for removed friends
		const validator = window.globalFormValidator || fv;
		if (validator) {
			// Remove validation for all possible friend fields beyond current friendCount
			for (let i = friendCount; i < 10; i++) { // Assume max 10 friends
				const fieldsToRemove = [
					`friend[${i}][name]`,
					`friend[${i}][email]`,
					`friend[${i}][mobile]`,
					`friend[${i}][country_code]`
				];
				
				fieldsToRemove.forEach(fieldName => {
					try {
						if (validator.elements && validator.elements[fieldName]) {
							validator.removeField(fieldName);
						}
					} catch (error) {
						// Ignore errors when removing non-existent fields
					}
				});
			}
		}
		
		// Dispatch event to reinitialize form validation
		const event = new CustomEvent('formValidationReinitialized');
		document.dispatchEvent(event);
	}, [friendCount]);

	// Setup event listeners for checkout page mode
	useEffect(() => {
		if (!sessionData.checkout_page) {
			return;
		}

		// Listen for custom feeQtyChanged event from SessionFeeQty components
		const handleFeeQtyChanged = (event) => {
			// Add a small delay to ensure DOM is updated
			setTimeout(() => {
				updateFriendCount();
			}, 50);
		};

		// Listen for reactFormLoaded event
		const handleReactFormLoaded = () => {
			setTimeout(() => {
				updateFriendCount();
			}, 100);
		};

		// Add event listeners
		document.addEventListener('feeQtyChanged', handleFeeQtyChanged);
		document.addEventListener('reactFormLoaded', handleReactFormLoaded);

		const observerTarget = document.querySelector('.ticket-display') || document.querySelector('#hook-sessions');
		const observer = observerTarget ? new MutationObserver(() => {
			updateFriendCount();
		}) : null;
		if (observer && observerTarget) {
			observer.observe(observerTarget, { childList: true, subtree: true });
		}

		// Cleanup function
		return () => {
			document.removeEventListener('feeQtyChanged', handleFeeQtyChanged);
			document.removeEventListener('reactFormLoaded', handleReactFormLoaded);
			if (observer) {
				observer.disconnect();
			}
		};
	}, [sessionData.checkout_page]);


	// Generate friend input components
	const generateFriendInputs = () => {
		const components = [];
		
		for (let i = 1; i <= friendCount; i++) {
			components.push(
				<div key={`friend-${i}`} className="friend-input-group mb-4">
					<div className="d-flex align-items-center gap-2 text-gray-700 border-bottom pb-2 border-dashed friend-title mb-3">
						<FriendIcon width={20} height={20} />
						<span className="my-0 fs-5 fw-bolder">{t('同行參加者 {{0}}', {0: i})}</span>
					</div>
					
					{/* Show HookSessionFeeSelect for this friend */}
					<HookSessionFeeSelect
						friendIndex={i - 1}
						displayIndex={i}
						funnel_page_id={props.funnel_page_id}
						lang={props.lang}
					/>
					<FriendName
						friendIndex={i - 1}
						form_component={{
							required: true,
							default_text_type: 'label',
							default_text: t('同行參加者姓名')
						}}
					/>
					<FriendEmail
						friendIndex={i - 1}
						form_component={{
							required: false,
							default_text_type: 'label',
							default_text: t('同行參加者電郵')
						}}
					/>
					<FriendMobile
						friendIndex={i - 1}
						form_component={{
							required: false,
							default_text_type: 'label',
							default_text: t('同行參加者手機')
						}}
					/>
				</div>
			);
		}
		
		return components;
	};

	// Get numParticipants from SessionFeeQty components or sessionData
	const getNumParticipantsFromSessionFeeQty = () => {
		// First, try to get from select elements with data-session-fee-qty="1"
		const qtySelects = document.querySelectorAll('select[data-session-fee-qty="1"]');
		if (qtySelects.length > 0) {
			const firstSelect = qtySelects[0];
			const maxParticipants = parseInt(firstSelect.getAttribute('data-max-participants'), 10);
			if (maxParticipants > 0) {
				return maxParticipants;
			}
		}
		
		// Second, try to get from .count elements with data-max-participants (from SessionFeeQty component)
		const countDivs = document.querySelectorAll('.count[data-max-participants]');
		if (countDivs.length > 0) {
			let maxParticipants = 0;
			countDivs.forEach(div => {
				const max = parseInt(div.getAttribute('data-max-participants'), 10);
				if (max > maxParticipants) {
					maxParticipants = max;
				}
			});
			if (maxParticipants > 0) {
				return maxParticipants;
			}
		}
		
		// Use sessionData.num_participants from API as fallback
		const fallbackValue = sessionData.num_participants || 1;
		return fallbackValue;
	};

	// Generate manual selection box for non-checkout pages
	const generateManualSelection = () => {
		if (sessionData.checkout_page) {
			return null;
		}
		
		// If reg_multiple_participants is false, no friends allowed
		if (sessionData.loaded && !sessionData.reg_multiple_participants) {
			return null;
		}
		
		// When no tix, use sessionData.num_participants and reg_multiple_participants
		// Always try to get from DOM first (in case DOM has more accurate data), then fallback to API
		let numParticipants;
		// Try to get from DOM first (this works even when no tickets are selected)
		const domNumParticipants = getNumParticipantsFromSessionFeeQty();
		if (domNumParticipants > 1) {
			// If DOM has a value > 1, use it (more reliable)
			numParticipants = domNumParticipants;
		} else if (sessionData.loaded) {
			// If DOM doesn't have a good value, use sessionData.num_participants from API
			numParticipants = (sessionData.num_participants !== undefined && sessionData.num_participants !== null) ? sessionData.num_participants : 1;
		} else {
			// Not loaded yet, use DOM value or default to 1
			numParticipants = domNumParticipants || 1;
		}
		
		// If numParticipants <= 1, no need to display 同行參加者 selection box
		if (numParticipants <= 1) {
			return null;
		}
		
		const options = [];
		// Generate options from 0 to (numParticipants - 1)
		// If numParticipants is 2, options 0 ("沒有") and 1 will be generated
		// If numParticipants is 3, options 0 ("沒有"), 1, and 2 will be generated
		const maxFriends = numParticipants - 1;
		for (let i = 0; i <= maxFriends; i++) {
			const displayText = i === 0 ? t('沒有') : t(`${i} 人`);
			options.push(
				<option key={i} value={i}>{displayText}</option>
			);
		}
		
		const selectionComponent = (
			<div className="d-flex flex-column gap-2 mb-3">
				<p className="fw-bolder flex-shrink-0 my-0 fs-5">{t('同行參加者')}</p>
				<select
					className="form-select flex-shrink-1"
					value={manualFriendCount}
					onChange={handleManualFriendCountChange}
				>
					{options}
				</select>
			</div>
		);
		
		return selectionComponent;
	};

	// Check if there are session fee entries to show HookSessionFeeSelect
	const hasSessionFeeEntries = () => {
		// Look for hidden input fields with the new structure: page_user_hook_session[hook_session_fees][sessionFeeId][qty]
		const qtyInputs = document.querySelectorAll('input.qty-hidden[name*="page_user_hook_session[hook_session_fees]"][name*="[qty]"]');
		const hasEntries = qtyInputs.length > 0;
		return hasEntries;
	};

	// For checkout page, check if have tix first
	if (sessionData.checkout_page) {
		// If reg_multiple_participants is false, don't show friend inputs at all
		if (sessionData.loaded && !sessionData.reg_multiple_participants) {
			return null;
		}
		
		const totalSelected = getTotalSelectedParticipants();
		if (totalSelected > 1 && friendCount > 0) {
			return (
				<div className="friend-inputs-container w-100">
					{friendCount > 0 && generateFriendInputs()}
				</div>
			);
		} else if (totalSelected === 0 || friendCount === 0) {
			return null;
		} else {
			// totalSelected === 1, only main participant, no friends needed
			return null;
		}
	}

	// If reg_multiple_participants is false and not checkout page, don't show friend inputs at all
	if (sessionData.loaded && !sessionData.reg_multiple_participants) {
		return null;
	}

	// For non-checkout page, check if manual selection should be shown
	const manualSelection = generateManualSelection();
	
	// If manual selection returns null and no friends, don't show anything
	if (!manualSelection && friendCount === 0) {
		return null;
	}

	// Show the manual selection box and friend inputs if any
	return (
		<div className="friend-inputs-container w-100">
			{manualSelection}
			{friendCount > 0 && generateFriendInputs()}
		</div>
	);
};

export default HookEventFriendInputs;